import { Skeleton } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { adminGetUsageOptions } from "@/api";
import { StatRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatCompact, formatNumber, formatUsd } from "@/i18n/format";
import { AdminPage } from "../AdminPage";
import { ActionsSummarySection } from "./ActionsSummarySection";
import { TopAgentsSection, TopToolsSection, UsageByModelSection, UsageByTeamSection } from "./UsageBreakdowns";
import { DEFAULT_RANGE, type RangeDays, RangeSelect, rangeStart } from "./usageRange";

/** /admin/usage: platform-wide AI usage for a selectable range. */
export function UsageOverviewPage() {
  const { t } = useTranslation("adminUsage");
  const [range, setRange] = useState<RangeDays>(DEFAULT_RANGE);
  const usage = useQuery({
    ...adminGetUsageOptions({ query: { start_date: rangeStart(range) } }),
    placeholderData: keepPreviousData,
  });
  const data = usage.data?.data;
  const totals = data?.totals;
  const tokensIn = totals?.tokensInput ?? 0;
  const tokensOut = totals?.tokensOutput ?? 0;

  return (
    <AdminPage title={t("overview.title")} subtitle={t("overview.subtitle")}>
      <RangeSelect label={t("range.label")} value={range} onChange={setRange} />
      {usage.isError && (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      )}
      {usage.isPending && <Skeleton className="h-16 w-full" />}
      {data && (
        <>
          <StatRow
            stats={[
              { label: t("overview.stats.requests"), value: formatNumber(totals?.requests ?? 0) },
              { label: t("overview.stats.activeUsers"), value: formatNumber(totals?.activeUsers ?? 0) },
              {
                label: t("overview.stats.tokens"),
                value: formatCompact(tokensIn + tokensOut),
                hint: t("overview.stats.tokensHint", {
                  input: formatCompact(tokensIn),
                  output: formatCompact(tokensOut),
                }),
              },
              { label: t("overview.stats.cost"), value: formatUsd(totals?.estimatedCostUsd ?? 0) },
            ]}
          />
          <UsageByTeamSection teams={data.byTeam ?? []} />
          <UsageByModelSection models={data.byModel ?? []} />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <TopToolsSection tools={data.topTools ?? []} />
            <TopAgentsSection agents={data.topAgents ?? []} />
          </div>
          <ActionsSummarySection startDate={rangeStart(range)} />
        </>
      )}
    </AdminPage>
  );
}
